import { MAX_KONTEXT_ZEICHEN, type TextAnker } from '@awf/shared';

export function gesamttext(container: Node): string {
  const lauf = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let text = '';
  let knoten: Node | null;
  while ((knoten = lauf.nextNode())) text += knoten.nodeValue ?? '';
  return text;
}

/**
 * Textknoten unter `wurzel`, die `bereich` mit ECHTEM Text beruehren -- Leerraum an den
 * Raendern eines nur teilweise erfassten Knotens zaehlt nicht. Gemeinsame Grundlage fuer alle
 * Stellen, die "Textknoten mit echtem Text in der Range" brauchen (D8, preflight-b2.md):
 * `ankerImText` und (`text-flaeche.tsx`) `beruehrteBloecke` bauten sonst dieselbe Schleife
 * zweimal.
 */
export function echteTextknoten(wurzel: Node, bereich: Range): Text[] {
  const ergebnis: Text[] = [];
  const lauf = document.createTreeWalker(wurzel, NodeFilter.SHOW_TEXT);
  let knoten: Node | null;
  while ((knoten = lauf.nextNode())) {
    if (!bereich.intersectsNode(knoten)) continue;
    const anfang = knoten === bereich.startContainer ? bereich.startOffset : 0;
    const ende =
      knoten === bereich.endContainer ? bereich.endOffset : (knoten.nodeValue?.length ?? 0);
    if ((knoten.nodeValue ?? '').slice(anfang, ende).trim() === '') continue;
    ergebnis.push(knoten as Text);
  }
  return ergebnis;
}

const BLOCK_AUSWAHL = 'p, ul, ol, blockquote, table, pre';

function istVerschachtelt(element: Element, container: Element): boolean {
  let eltern = element.parentElement;
  while (eltern && eltern !== container) {
    if (eltern.matches(BLOCK_AUSWAHL)) return true;
    eltern = eltern.parentElement;
  }
  return false;
}

const ABSCHNITT = '[data-abschnitt-id]';

function abschnittVonKnoten(knoten: Node): string | null {
  const element = knoten instanceof Element ? knoten : knoten.parentElement;
  return element?.closest(ABSCHNITT)?.getAttribute('data-abschnitt-id') ?? null;
}

/**
 * Teil B: Ein Abschnitt ist kein einzelner Container mehr, sondern alle Elemente mit seinem
 * `data-abschnitt-id` -- je Block eines, dazu die Baustein-Beschriftung. Ein Baustein darf
 * Abschnitte ueberspannen und traegt dann Elemente beider. Der "gerenderte Text eines
 * Abschnitts" (Spec §5, E2 neu gefasst) ist deshalb die Verkettung dieser Textknoten in
 * Dokumentreihenfolge; `abschnittsTexte` in `abschnitte.ts` baut denselben Text ohne DOM.
 */
export function textknotenDesAbschnitts(wurzel: Element, abschnittId: string): Text[] {
  const lauf = document.createTreeWalker(wurzel, NodeFilter.SHOW_TEXT);
  const knoten: Text[] = [];
  let aktuell: Node | null;
  while ((aktuell = lauf.nextNode())) {
    if (abschnittVonKnoten(aktuell) === abschnittId) knoten.push(aktuell as Text);
  }
  return knoten;
}

export function abschnittsTextIm(wurzel: Element, abschnittId: string): string {
  return textknotenDesAbschnitts(wurzel, abschnittId)
    .map((k) => k.nodeValue ?? '')
    .join('');
}

/** Wie viel Abschnittstext liegt vor dem Punkt? Text anderer Abschnitte zaehlt nicht. */
function punktOffset(knoten: readonly Text[], punkt: Node, offset: number): number {
  let summe = 0;
  for (const k of knoten) {
    const laenge = k.nodeValue?.length ?? 0;
    if (k === punkt) return summe + Math.min(offset, laenge);
    const vergleich = document.createRange();
    vergleich.setStart(punkt, offset);
    // `comparePoint >= 0`: der Textknoten beginnt am oder hinter dem Punkt.
    if (vergleich.comparePoint(k, 0) >= 0) return summe;
    summe += laenge;
  }
  return summe;
}

/**
 * Aeusserstes BLOCK_AUSWAHL-Element ueber einem Textknoten, begrenzt durch `wurzel`.
 * Ueberschriften, `hr`, html und die Baustein-Beschriftung matchen BLOCK_AUSWAHL nicht
 * und liefern deshalb null -- sie zaehlen nicht als eigener Absatz (Spec E2).
 */
function aeusserstesBlockElement(knoten: Text, wurzel: Element): Element | null {
  let element = knoten.parentElement?.closest(BLOCK_AUSWAHL) ?? null;
  while (element && istVerschachtelt(element, wurzel)) {
    element = element.parentElement?.closest(BLOCK_AUSWAHL) ?? null;
  }
  return element;
}

/**
 * Zaehlt Absaetze ueber die Textknoten des Abschnitts, nicht ueber einen einzelnen Container
 * (Teil B: ein Abschnitt ist kein einzelner Container mehr). Liegt der Beginn in einer
 * Ueberschrift oder Beschriftung (kein BLOCK_AUSWAHL-Treffer), gilt die Nummer des letzten
 * vorangehenden Absatzes, mindestens 1 -- ergibt sich von selbst, weil solche Knoten keinen
 * eigenen Eintrag in `gesehen` erzeugen.
 */
function blockNummerIm(knoten: readonly Text[], wurzel: Element, von: number): number {
  const gesehen: Element[] = [];
  let summe = 0;
  for (const k of knoten) {
    const block = aeusserstesBlockElement(k, wurzel);
    if (block && !gesehen.includes(block)) gesehen.push(block);
    summe += k.nodeValue?.length ?? 0;
    if (summe > von) break;
  }
  return Math.max(1, gesehen.length);
}

export function ankerImText(wurzel: Element, bereich: Range): TextAnker | 'grenze' | null {
  // Welche Abschnitte traegt die Auswahl mit ECHTEM Text? Leerraum und Text ohne Abschnitt
  // (Bildnamen, Knoepfe) zaehlen nicht -- Chrome haengt Auswahlenden gern dort an.
  const ids = new Set<string>();
  for (const knoten of echteTextknoten(wurzel, bereich)) {
    const id = abschnittVonKnoten(knoten);
    if (id) ids.add(id);
  }
  if (ids.size > 1) return 'grenze';
  const [abschnittId] = [...ids];
  if (!abschnittId) return null;

  const knoten = textknotenDesAbschnitts(wurzel, abschnittId);
  const text = knoten.map((k) => k.nodeValue ?? '').join('');
  const start = punktOffset(knoten, bereich.startContainer, bereich.startOffset);
  const ende = punktOffset(knoten, bereich.endContainer, bereich.endOffset);
  const roh = text.slice(start, ende);
  const zitat = roh.trim();
  if (zitat === '') return null;
  // `von` aus dem ungetrimmten Beginn plus fuehrendem Leerraum (wie zuvor `markierungAusBereich`).
  const von = start + (roh.length - roh.trimStart().length);
  const bis = von + zitat.length;
  return {
    zitat,
    kontextDavor: text.slice(Math.max(0, von - MAX_KONTEXT_ZEICHEN), von),
    kontextDanach: text.slice(bis, bis + MAX_KONTEXT_ZEICHEN),
    abschnittId,
    absatzNummer: blockNummerIm(knoten, wurzel, von),
    von,
    bis,
  };
}

export function bereichImAbschnitt(
  wurzel: Element,
  abschnittId: string,
  von: number,
  bis: number,
): Range | null {
  if (!Number.isInteger(von) || !Number.isInteger(bis) || von < 0 || bis <= von) return null;
  const bereich = document.createRange();
  let gelaufen = 0;
  let startGesetzt = false;
  for (const k of textknotenDesAbschnitts(wurzel, abschnittId)) {
    const laenge = k.nodeValue?.length ?? 0;
    if (!startGesetzt && von <= gelaufen + laenge) {
      bereich.setStart(k, von - gelaufen);
      startGesetzt = true;
    }
    if (startGesetzt && bis <= gelaufen + laenge) {
      bereich.setEnd(k, bis - gelaufen);
      return bereich;
    }
    gelaufen += laenge;
  }
  return null;
}

/**
 * Kuerzt einen Anker auf den Anfang seines Zitats (Kommentar zu einem ganzen Block ueber
 * `MAX_ZITAT_ZEICHEN`, Karte j3gkZH2Hgc6K57Oa8f1F). Das Zitat bleibt woertlicher Text an
 * `von`/`bis`: Das Backend verlangt `bis - von === zitat.length`, und die Oberflaeche findet die
 * Stelle nur so wieder. Ein angehaengtes Auslassungszeichen ginge deshalb nicht. Was abgeschnitten
 * wird, steht als Kontext danach, so wie ihn `ankerImText` fuer die kuerzere Auswahl gebildet
 * haette; die Absatznummer bleibt, sie haengt nur am Beginn.
 */
export function kuerzeAnker(anker: TextAnker, hoechstens: number): TextAnker {
  if (anker.zitat.length <= hoechstens) return anker;
  const zitat = anker.zitat.slice(0, hoechstens).trimEnd();
  return {
    ...anker,
    zitat,
    bis: anker.von + zitat.length,
    kontextDanach: (anker.zitat.slice(zitat.length) + anker.kontextDanach).slice(
      0,
      MAX_KONTEXT_ZEICHEN,
    ),
  };
}

/**
 * Findet die Passage eines Ankers im jetzt gezeigten Text wieder (Markieren im offenen Block,
 * Karte j3gkZH2Hgc6K57Oa8f1F): erst an denselben Offsets, sonst als einziges Vorkommen ihres
 * Zitats im Abschnitt. Kommt das Zitat mehrfach oder gar nicht vor, ist die Stelle nicht
 * eindeutig, und es gibt keinen Bereich.
 */
export function wiederfinden(wurzel: Element, anker: TextAnker): Range | null {
  const text = abschnittsTextIm(wurzel, anker.abschnittId);
  let von = anker.von;
  if (text.slice(von, anker.bis) !== anker.zitat) {
    const erstes = text.indexOf(anker.zitat);
    if (erstes < 0 || text.indexOf(anker.zitat, erstes + 1) >= 0) return null;
    von = erstes;
  }
  return bereichImAbschnitt(wurzel, anker.abschnittId, von, von + anker.zitat.length);
}
